import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UserInfo } from '@contexts/users/types';
import axios from 'axios';
import configuration from '@config';
import { isInsufficientPermissionsError, parseUserInfo } from './auth-utils';
import { setFaroUser } from '@src/telemetry/faro';

export interface AuthenticationState {
  isAuthenticated: boolean;
  accessToken?: string;
}

export interface IAuthContext {
  authStatus: AuthenticationState;
  setIsPrivateRoute: React.Dispatch<React.SetStateAction<boolean>>;
  user?: UserInfo;
  logout: () => void;
  loading: boolean;
  token?: string;
}

const AuthContext = createContext<IAuthContext>({} as IAuthContext);
export const useAuth = () => useContext(AuthContext);

interface AuthWrapperProps {
  children: React.ReactNode;
}
const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
  const [user, setUser] = useState<UserInfo | undefined>(undefined);
  const [token, setToken] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isPrivateRoute, setIsPrivateRoute] = useState(false);
  const isPrivateRouteRef = useRef(false);
  const [expiresIn, setExpiresIn] = useState<number | undefined>(undefined);

  useEffect(() => {
    isPrivateRouteRef.current = isPrivateRoute;
  }, [isPrivateRoute]);

  const url = new URL(import.meta.url);
  const protocol = url.protocol;
  const host = url.host;
  const currentDomain = `${protocol}//${host}`;
  const serverPort = configuration.SERVER_PORT ?? '5001';
  const serverBaseURL = `${currentDomain.replace(
    /:\d+/,
    `${configuration.IS_IN_PRODUCTION === true ? '' : `:${serverPort}`}`,
  )}/api/auth`;

  const serverApiClient = useMemo(
    () =>
      axios.create({
        baseURL: serverBaseURL,
        withCredentials: true,
      }),
    [serverBaseURL],
  );

  const logout = useCallback(async () => {
    try {
      await serverApiClient.get('/logout');
      window.location.href = `${serverBaseURL}/login`;
      setUser(undefined);
      setFaroUser(undefined);
      setIsAuthenticated(false);
    } catch {
      window.location.reload();
    }
  }, [serverApiClient, serverBaseURL]);

  const checkSession = useCallback(async () => {
    try {
      await serverApiClient.get('/');

      const { data } = await serverApiClient.get('/profile');
      const { user, token } = data.userAuthData;
      const parsedUser = parseUserInfo(user);
      setUser(parsedUser);
      setFaroUser(parsedUser?.id);

      const accessToken = token.access_token;
      setToken(accessToken);

      if (accessToken) {
        setIsAuthenticated(true);
      }

      if (token.expires_in <= 0) {
        return logout();
      }

      setExpiresIn(token.expires_in);
    } catch (error: unknown) {
      if (isInsufficientPermissionsError(error)) {
        return (window.location.href = `/errors/403`);
      }

      const axiosError = error as { response?: { data?: { message?: string }; status?: number } };
      if (axiosError?.response?.status === 401 && isPrivateRoute) {
        return logout();
      }

      if (isPrivateRouteRef.current) {
        return (window.location.href = `${serverBaseURL}/login`);
      }
    } finally {
      setLoading(false);
    }
  }, [serverBaseURL, serverApiClient, logout]);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  useEffect(() => {
    if (typeof expiresIn === 'number' && isAuthenticated) {
      const refreshTime = Math.max((expiresIn - 60) * 1000, 30000);
      const timeoutId = setTimeout(checkSession, refreshTime);
      return () => clearTimeout(timeoutId);
    }
  }, [expiresIn, isAuthenticated, checkSession]);

  const contextValue: IAuthContext = {
    authStatus: { isAuthenticated, accessToken: token },
    user,
    logout,
    loading,
    token,
    setIsPrivateRoute,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export default AuthWrapper;
